import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, Image, Sparkles, ChevronDown, X, ListChecks } from 'lucide-react';
import { WEEKS_DATA } from '../data/weeksData';

function Navigation() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when search is shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Search through all tasks
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    WEEKS_DATA.forEach((week) => {
      week.daily_tasks.forEach((task) => {
        if (
          task.title.toLowerCase().includes(lowerQuery) ||
          task.description.toLowerCase().includes(lowerQuery) ||
          week.theme.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            weekNumber: week.week_number,
            weekTheme: week.theme,
            day: task.day,
            title: task.title,
            id: task.id
          });
        }
      });
    });

    setSearchResults(results.slice(0, 8)); // Limit to 8 results
  };

  const navigateToTask = (weekNumber) => {
    navigate(`/week/${weekNumber}`);
    setIsOpen(false);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-primary/10 sticky top-0 z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/dashboard')}
            data-testid="nav-logo"
          >
            <Sparkles className="w-6 h-6 text-primary" />
            <span className="text-xl font-serif font-bold text-secondary-900">Radiant 40</span>
          </div>

          {/* Menu Button */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 hover:bg-primary/20 text-primary font-medium transition-all duration-200"
              data-testid="nav-menu-button"
            >
              <Menu className="w-5 h-5" />
              <span>Menu</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Search Section */}
                <div className="p-4 border-b border-gray-100">
                  <button
                    onClick={() => setShowSearch(!showSearch)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    data-testid="nav-search-toggle"
                  >
                    <Search className="w-5 h-5 text-primary" />
                    <span className="font-medium text-secondary-900">Search Tasks</span>
                  </button>

                  {showSearch && (
                    <div className="mt-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search by task name or topic..."
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                          data-testid="nav-search-input"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <div className="mt-2 max-h-64 overflow-y-auto">
                          {searchResults.map((result, index) => (
                            <button
                              key={`${result.weekNumber}-${result.day}-${index}`}
                              onClick={() => navigateToTask(result.weekNumber)}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary/10 transition-colors"
                              data-testid={`search-result-${index}`}
                            >
                              <div className="font-medium text-secondary-900 text-sm">{result.title}</div>
                              <div className="text-xs text-gray-500">
                                Week {result.weekNumber} • Day {result.day} • {result.weekTheme}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchQuery.length >= 2 && searchResults.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-3">No tasks found</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Menu Items */}
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate('/welcome');
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    data-testid="nav-welcome-link"
                  >
                    <Sparkles className="w-5 h-5 text-accent-600" />
                    <div>
                      <span className="font-medium text-secondary-900">Welcome Page</span>
                      <p className="text-xs text-gray-500">Revisit your welcome message</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/daily-essentials');
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    data-testid="nav-essentials-link"
                  >
                    <ListChecks className="w-5 h-5 text-emerald-500" />
                    <div>
                      <span className="font-medium text-secondary-900">8 Daily Essentials</span>
                      <p className="text-xs text-gray-500">Your foundation practices</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/gallery');
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                    data-testid="nav-gallery-link"
                  >
                    <Image className="w-5 h-5 text-pink-500" />
                    <div>
                      <span className="font-medium text-secondary-900">Photo Gallery</span>
                      <p className="text-xs text-gray-500">Upload and view your journey photos</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navigation;
